import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import RecipeCard from '../components/RecipeCard';
import api from '../utils/api';
import './Search.css';

const DIFFICULTY_OPTIONS = ['easy', 'medium', 'hard'];
const TIME_OPTIONS = [
{ label: 'Under 15 min', value: '15' },
{ label: 'Under 30 min', value: '30' },
{ label: 'Under 45 min', value: '45' },
{ label: 'Under 60 min', value: '60' },
{ label: 'Under 90 min', value: '90' },
];

export default function Search() {
const [searchParams, setSearchParams] = useSearchParams();
const [recipes, setRecipes] = useState([]);
const [loading, setLoading] = useState(false);
const [pagination, setPagination] = useState({});
const [inputValue, setInputValue] = useState(searchParams.get('q') || '');
const [showFilters, setShowFilters] = useState(false);

const q = searchParams.get('q') || '';
const category = searchParams.get('category') || '';
const difficulty = searchParams.get('difficulty') || '';
const maxTime = searchParams.get('maxTime') || '';
const page = parseInt(searchParams.get('page') || '1');

const fetchRecipes = useCallback(async () => {
setLoading(true);
try {
const params = new URLSearchParams({ page, limit: 20 });
if (q) params.set('search', q);
if (category) params.set('category', category);
if (difficulty) params.set('difficulty', difficulty);
if (maxTime) params.set('maxTime', maxTime);
const res = await api.get(`/recipes?${params}`);
setRecipes(res.data.recipes || []);
setPagination(res.data.pagination || {});
} catch {
setRecipes([]);
} finally {
setLoading(false);
}
}, [q, category, difficulty, maxTime, page]);

useEffect(() => { fetchRecipes(); }, [fetchRecipes]);
useEffect(() => { setInputValue(q); }, [q]);

const handleSearch = (e) => {
e.preventDefault();
const newParams = new URLSearchParams(searchParams);
if (inputValue.trim()) newParams.set('q', inputValue.trim());
else newParams.delete('q');
newParams.set('page', '1');
setSearchParams(newParams);
};

const setFilter = (key, value) => {
const newParams = new URLSearchParams(searchParams);
if (value) newParams.set(key, value);
else newParams.delete(key);
newParams.set('page', '1');
setSearchParams(newParams);
};

const clearFilters = () => { setInputValue(''); setSearchParams({}); };

const hasFilters = q || category || difficulty || maxTime;
const activeFilterCount = [q, category, difficulty, maxTime].filter(Boolean).length;
const pageTitle = category
? category.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
: q ? `Results for "${q}"` : 'All Recipes';

return (
<div className="search-page">
<div className="search-page__header">
<div className="container">
<form onSubmit={handleSearch} className="search-page__form">
<div className="search-page__input-wrap">
<svg className="search-page__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
<circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
</svg>
<input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)}
placeholder="Search recipes, ingredients, methods..." className="search-page__input" autoFocus />
{inputValue && <button type="button" className="search-page__clear" onClick={() => setInputValue('')}></button>}
</div>
<button type="submit" className="btn btn-primary">Search</button>
<button type="button"
className={`btn btn-outline search-page__filter-toggle ${showFilters ? 'active' : ''}`}
onClick={() => setShowFilters(!showFilters)}>
Filters {activeFilterCount > 0 && <span className="search-page__filter-count">{activeFilterCount}</span>}
</button>
</form>
{showFilters && (
<div className="search-page__filters">
<div className="search-page__filter-group">
<label>Difficulty</label>
<div className="search-page__filter-pills">
{DIFFICULTY_OPTIONS.map(d => (
<button key={d} className={`search-page__pill ${difficulty === d ? 'active' : ''}`}
onClick={() => setFilter('difficulty', difficulty === d ? '' : d)}>
{d.charAt(0).toUpperCase() + d.slice(1)}
</button>
))}
</div>
</div>
<div className="search-page__filter-group">
<label>Total Time</label>
<div className="search-page__filter-pills">
{TIME_OPTIONS.map(t => (
<button key={t.value} className={`search-page__pill ${maxTime === t.value ? 'active' : ''}`}
onClick={() => setFilter('maxTime', maxTime === t.value ? '' : t.value)}>
{t.label}
</button>
))}
</div>
</div>
{hasFilters && <button className="btn btn-ghost" onClick={clearFilters}>Clear all filters</button>}
</div>
)}
</div>
</div>

<div className="container">
<div className="search-page__results-header">
<h1 className="search-page__title">{pageTitle}</h1>
<div className="search-page__result-meta">
{pagination.total > 0 && <span className="search-page__count">{pagination.total} recipe{pagination.total !== 1 ? 's' : ''}</span>}
</div>
</div>
{hasFilters && (
<div className="search-page__active-filters">
{q && <span className="search-page__chip">"{q}" <button onClick={() => { setInputValue(''); setFilter('q', ''); }}></button></span>}
{category && <span className="search-page__chip">Category: {category} <button onClick={() => setFilter('category', '')}></button></span>}
{difficulty && <span className="search-page__chip">Difficulty: {difficulty} <button onClick={() => setFilter('difficulty', '')}></button></span>}
{maxTime && <span className="search-page__chip">Under {maxTime} min <button onClick={() => setFilter('maxTime', '')}></button></span>}
</div>
)}
{loading ? (
<div className="page-loading"><div className="spinner" /></div>
) : recipes.length === 0 ? (
<div className="empty-state">
<div className="empty-icon"></div>
<h3>No recipes found</h3>
<p>{q ? `No recipes match "${q}". Try different keywords or fewer filters.` : 'No recipes match these filters.'}</p>
</div>
) : (
<>
<div className="search-page__grid">
{recipes.map(recipe => (<RecipeCard key={recipe.id} recipe={recipe} />))}
</div>
{pagination.pages > 1 && (
<div className="search-page__pagination">
{page > 1 && <button className="btn btn-outline" onClick={() => { const p = new URLSearchParams(searchParams); p.set('page', page - 1); setSearchParams(p); }}>← Previous</button>}
<span className="search-page__page-info">Page {page} of {pagination.pages}</span>
{page < pagination.pages && <button className="btn btn-outline" onClick={() => { const p = new URLSearchParams(searchParams); p.set('page', page + 1); setSearchParams(p); }}>Next →</button>}
</div>
)}
</>
)}
</div>
</div>
);
}
